import { z } from 'zod';
/** Working groups, programs, calendars, temporary events and scenes (AFM requirements b, h, o). */
export const GroupKind = z.enum([
    'street',
    'intersection',
    'pedestrian_crossing',
    'parking',
    'pedestrian',
    'park',
    'festive',
    'other',
]);
export const RunMode = z.enum(['central', 'device', 'both', 'off']);
const Time = z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/);
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const MonthDay = z.string().regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/);
export const StepAt = z.discriminatedUnion('type', [
    z.object({ type: z.literal('time'), time: Time }),
    z.object({ type: z.literal('sunset'), offset_min: z.number().int().min(-240).max(240) }),
    z.object({ type: z.literal('sunrise'), offset_min: z.number().int().min(-240).max(240) }),
]);
export const ProgramStep = z.object({ at: StepAt, level: z.number().int().min(0).max(100) });
export const Program = z.object({
    id: z.uuid(),
    client_id: z.uuid().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    steps: z.array(ProgramStep),
    groups: z.number().int(),
    can_manage: z.boolean(),
});
export const ProgramList = z.object({ items: z.array(Program) });
export const ProgramSave = z.object({
    client_id: z.uuid().nullable(),
    name: z.string().trim().min(1).max(120),
    description: z.string().max(2000).nullable().optional(),
    steps: z.array(ProgramStep).min(1).max(24),
});
export const CalendarRule = z.object({
    id: z.uuid(),
    program_id: z.uuid(),
    label: z.string().nullable(),
    kind: z.enum(['weekly', 'date_range', 'yearly']),
    weekdays: z.array(z.number().int().min(0).max(6)).nullable(),
    date_from: Day.nullable(),
    date_to: Day.nullable(),
    month_day_from: MonthDay.nullable(),
    month_day_to: MonthDay.nullable(),
    priority: z.number().int(),
});
export const CalendarRuleSave = CalendarRule.omit({ id: true }).extend({
    label: z.string().max(120).nullable().optional(),
    weekdays: z.array(z.number().int().min(0).max(6)).min(1).max(7).nullable().optional(),
    date_from: Day.nullable().optional(),
    date_to: Day.nullable().optional(),
    month_day_from: MonthDay.nullable().optional(),
    month_day_to: MonthDay.nullable().optional(),
    priority: z.number().int().min(0).max(1000).optional(),
});
export const GroupEvent = z.object({
    id: z.uuid(),
    label: z.string().nullable(),
    starts_at: z.string(),
    ends_at: z.string(),
    level: z.number().int().nullable(),
    program_id: z.uuid().nullable(),
    scene_id: z.uuid().nullable(),
    cancelled_at: z.string().nullable(),
});
export const GroupEventSave = z
    .object({
    label: z.string().max(120).nullable().optional(),
    starts_at: z.iso.datetime({ offset: true }),
    ends_at: z.iso.datetime({ offset: true }),
    level: z.number().int().min(0).max(100).nullable().optional(),
    program_id: z.uuid().nullable().optional(),
})
    .refine((e) => (e.level ?? null) !== null || (e.program_id ?? null) !== null, {
    message: 'level or program_id',
});
/** What a group prescribes now (Central's evaluation of program, calendar and events). */
export const GroupNow = z.object({
    level: z.number().int().nullable(),
    source: z.enum(['event', 'calendar', 'program', 'none']),
    since: z.string().nullable(),
    next_at: z.string().nullable(),
    next_level: z.number().int().nullable(),
    program_id: z.uuid().nullable(),
});
export const Group = z.object({
    id: z.uuid(),
    client_id: z.uuid(),
    client_name: z.string(),
    name: z.string(),
    kind: GroupKind,
    description: z.string().nullable(),
    color: z.string().nullable(),
    program_id: z.uuid().nullable(),
    run_mode: RunMode,
    devices: z.number().int(),
    now: GroupNow,
    /** device-side program state of the members: counts per sync status */
    sync: z.record(z.string(), z.number().int()),
    can_manage: z.boolean(),
});
export const GroupList = z.object({ items: z.array(Group) });
export const GroupSave = z.object({
    client_id: z.uuid(),
    name: z.string().trim().min(1).max(120),
    kind: GroupKind,
    description: z.string().max(2000).nullable().optional(),
    color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .nullable()
        .optional(),
    program_id: z.uuid().nullable().optional(),
    run_mode: RunMode.optional(),
});
export const GroupDetail = Group.extend({
    rules: z.array(CalendarRule),
    events: z.array(GroupEvent),
    members: z.array(z.object({
        device_id: z.uuid(),
        name: z.string(),
        site_name: z.string().nullable(),
        presence: z.string(),
        dimmer_pct: z.number().nullable(),
        desired_level: z.number().int().nullable(),
        schedule_state: z.enum(['none', 'pending', 'applied', 'failed']),
        sync_status: z.string().nullable(),
        sync_detail: z.record(z.string(), z.unknown()).nullable(),
    })),
});
/** PUT /api/v1/groups/:id/members — add (move) devices into the group; DELETE … removes them. */
export const GroupMembers = z.object({ device_ids: z.array(z.uuid()).min(1).max(5000) });
export const SceneAction = z.object({ group_id: z.uuid(), level: z.number().int().min(0).max(100) });
export const Scene = z.object({
    id: z.uuid(),
    client_id: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    actions: z.array(SceneAction),
    duration_min: z.number().int().nullable(),
    can_manage: z.boolean(),
    can_execute: z.boolean(),
});
export const SceneList = z.object({ items: z.array(Scene) });
export const SceneSave = z.object({
    client_id: z.uuid(),
    name: z.string().trim().min(1).max(120),
    description: z.string().max(2000).nullable().optional(),
    actions: z.array(SceneAction).min(1).max(200),
    duration_min: z.number().int().min(1).max(525600).nullable().optional(),
});
export const SceneRunResult = z.object({ events: z.number().int(), devices: z.number().int() });
/** Program preview: the switch points of the next nights for a client position (UI chart). */
export const ProgramPreview = z.object({
    nights: z.array(z.object({ anchor: Day, events: z.array(z.object({ at: z.string(), level: z.number().int() })) })),
});
/** Automation rule: a measurement (sensor, platform, controller) gives a working group a level for a while. */
export const AutomationOperator = z.enum(['>', '>=', '<', '<=', '=']);
export const AutomationRule = z.object({
    id: z.uuid(),
    client_id: z.uuid(),
    name: z.string(),
    enabled: z.boolean(),
    source_device_id: z.uuid().nullable(),
    source_device_name: z.string().nullable(),
    source_group_id: z.uuid().nullable(),
    metric_key: z.string(),
    operator: AutomationOperator,
    threshold: z.number(),
    max_age_s: z.number().int(),
    target_group_id: z.uuid(),
    level: z.number().int(),
    duration_min: z.number().int(),
    cooldown_min: z.number().int(),
    last_fired_at: z.string().nullable(),
    last_value: z.number().nullable(),
    fired_count: z.number().int(),
    can_manage: z.boolean(),
});
export const AutomationRuleList = z.object({ items: z.array(AutomationRule) });
export const AutomationRuleSave = z
    .object({
    client_id: z.uuid(),
    name: z.string().trim().min(1).max(120),
    enabled: z.boolean().optional(),
    source_device_id: z.uuid().nullable().optional(),
    source_group_id: z.uuid().nullable().optional(),
    metric_key: z.string().regex(/^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/),
    operator: AutomationOperator,
    threshold: z.number().finite(),
    max_age_s: z.number().int().min(10).max(86400).optional(),
    target_group_id: z.uuid(),
    level: z.number().int().min(0).max(100),
    duration_min: z.number().int().min(1).max(1440),
    cooldown_min: z.number().int().min(0).max(1440).optional(),
})
    .refine((r) => (r.source_device_id ?? null) !== null || (r.source_group_id ?? null) !== null, {
    message: 'source',
});
//# sourceMappingURL=groups.js.map